import { useEffect, useState } from "react";
import { useTranslation } from "@/common/providers/i18n-provider";
import {
  MAX_CSV_DESCRIPTION_RULE_ID_LENGTH,
  MAX_CSV_DESCRIPTION_RULE_PATTERN_LENGTH,
  MAX_CSV_DESCRIPTION_RULES,
  type CsvDescriptionRule,
} from "@/lib/csv-description-rules";
import {
  CSV_PROFILE_VERSION,
  MAX_CSV_PROFILES,
  MAX_CSV_PROFILE_CATEGORIES,
  MAX_CSV_PROFILE_CATEGORY_LENGTH,
  MAX_CSV_PROFILE_HEADERS,
  MAX_CSV_PROFILE_HEADER_LENGTH,
  MAX_CSV_PROFILE_LABEL_LENGTH,
  checkProfileCompatibility,
  createProfileId,
  indexMappingToHeaderNames,
  type CsvMappingProfile,
  type ProfileCompatibility,
} from "@/lib/csv-mapping-profiles";
import { downloadTextFile } from "@/lib/clipboard";
import { serializeCsvProfileFile } from "@/lib/csv-profile-file";
import {
  trackCsvProfileAction,
  type CsvProfileExportScope,
} from "@/lib/tools-analytics";
import {
  DEFAULT_CURRENCY,
  isValidAccount,
  isValidCommodity,
  type ColumnMapping,
  type CsvOutputMode,
} from "@/lib/csv-to-beancount";
import type { DateOrder } from "@/lib/import-normalize";
import {
  loadMappingProfiles,
  resolveProfileStorage,
  saveMappingProfiles,
  SAVE_PROFILES_ERROR_KEYS,
  type ProfileStorage,
  type SaveProfilesResult,
} from "./csv-profile-storage";
import { CsvProfileImportControls } from "./csv-profile-file-controls";

/** The widget settings one profile snapshot carries. No statement data. */
export interface CsvProfileSettings {
  mapping: ColumnMapping;
  sourceAccount: string;
  counterAccount: string;
  currency: string;
  invertAmounts: boolean;
  dateOrder: DateOrder | undefined;
  outputMode: CsvOutputMode;
  /**
   * Explicit category-value → destination-account assignments, blanks
   * included: a blank value reads as "fall back" and never persists. Absent
   * (callers from before categorization) reads as no assignments.
   */
  categoryAccounts?: Record<string, string>;
  /**
   * Ordered literal description → account rules. Absent reads as no rules;
   * loading a profile without this field resets the widget's active rules
   * to empty so a rule-free profile never keeps stale rules. Persisted only
   * on explicit Save/Update — never analytics, URLs, rows or output text.
   */
  descriptionRules?: CsvDescriptionRule[];
}

/**
 * Trim, drop blanks, truncate, dedupe and cap raw headers into a vocabulary
 * the profile schema accepts, so a snapshot can never save a profile that
 * fails to reload. Mapping names outside the vocabulary resolve to `null`
 * on load rather than rejecting, so over-wide files degrade gracefully.
 */
function snapshotHeaders(headers: readonly string[]): string[] {
  const cleaned: string[] = [];
  for (const header of headers) {
    const name = header.trim().slice(0, MAX_CSV_PROFILE_HEADER_LENGTH);
    if (name === "" || cleaned.includes(name)) continue;
    cleaned.push(name);
    if (cleaned.length >= MAX_CSV_PROFILE_HEADERS) break;
  }
  return cleaned;
}

function settingsValid(settings: CsvProfileSettings): boolean {
  if (
    !isValidAccount(settings.sourceAccount.trim()) ||
    !isValidAccount(settings.counterAccount.trim()) ||
    !isValidCommodity(settings.currency.trim() || DEFAULT_CURRENCY)
  ) {
    return false;
  }
  // Blanks read as "fall back" (see `snapshotCategoryAccounts`); anything
  // typed must already be an account the emitter accepts, or the saved
  // profile would block conversion on load.
  const categoriesValid = Object.values(settings.categoryAccounts ?? {}).every(
    (account) => account.trim() === "" || isValidAccount(account.trim()),
  );
  return categoriesValid && descriptionRulesValid(settings.descriptionRules);
}

/**
 * Whether the live rules may be snapshotted. Mirrors the stored-rule limits
 * (`parseDescriptionRules` in `@/lib/csv-mapping-profiles`) so a snapshot
 * can never save a profile that fails to reload — except blank patterns,
 * which read as deletable editor drafts: an enabled blank would block
 * conversion (`contains ""` matches every row), so it blocks saving too,
 * while a disabled blank is inert and simply dropped on save.
 */
function descriptionRulesValid(
  rules: readonly CsvDescriptionRule[] | undefined,
): boolean {
  if (rules === undefined) return true;
  if (rules.length > MAX_CSV_DESCRIPTION_RULES) return false;
  const seenIds = new Set<string>();
  for (const rule of rules) {
    const id = rule.id.trim();
    const pattern = rule.pattern.trim();
    if (typeof rule.enabled !== "boolean") return false;
    if (pattern === "") {
      if (rule.enabled) return false;
      continue;
    }
    if (id === "" || id.length > MAX_CSV_DESCRIPTION_RULE_ID_LENGTH) {
      return false;
    }
    if (rule.operator !== "equals" && rule.operator !== "contains") {
      return false;
    }
    if (pattern.length > MAX_CSV_DESCRIPTION_RULE_PATTERN_LENGTH) return false;
    if (!isValidAccount(rule.account.trim())) return false;
    if (seenIds.has(id)) return false;
    seenIds.add(id);
  }
  return true;
}

/**
 * Sanitize live rules into persistable configuration, preserving order,
 * identity and enabled state. Blank-pattern drafts never persist (like
 * blank category values), and the rest is capped — so a snapshot always
 * reloads exactly what was saved. Only ever called with `settingsValid`
 * already accepted, so every kept rule is one the store accepts.
 */
function snapshotDescriptionRules(
  rules: readonly CsvDescriptionRule[] | undefined,
): CsvDescriptionRule[] {
  const kept: CsvDescriptionRule[] = [];
  for (const rule of rules ?? []) {
    if (kept.length >= MAX_CSV_DESCRIPTION_RULES) break;
    const id = rule.id.trim();
    const pattern = rule.pattern.trim();
    const account = rule.account.trim();
    if (pattern === "") continue;
    kept.push({ id, enabled: rule.enabled, operator: rule.operator, pattern, account });
  }
  return kept;
}

/**
 * Sanitize live assignments into persistable configuration: blank values
 * read as "fall back" and never persist, over-long names are dropped, and
 * the rest is capped — so a snapshot always reloads exactly what was saved.
 * Only ever called with settings `settingsValid` already accepted, so every
 * kept value is an account the emitter accepts.
 */
function snapshotCategoryAccounts(
  assignments: Record<string, string> | undefined,
): Record<string, string> {
  const kept: Record<string, string> = {};
  let count = 0;
  for (const [rawKey, rawValue] of Object.entries(assignments ?? {})) {
    if (count >= MAX_CSV_PROFILE_CATEGORIES) break;
    const key = rawKey.trim();
    const value = rawValue.trim();
    if (key === "" || key.length > MAX_CSV_PROFILE_CATEGORY_LENGTH) continue;
    if (value === "") continue;
    kept[key] = value;
    count += 1;
  }
  return kept;
}

/**
 * Explicit save / load / delete controls for browser-local mapping profiles.
 *
 * The list starts empty and fills in a mount effect: the server and the first
 * client render agree (no hydration mismatch), and profiles appear once the
 * browser store is readable. Nothing here touches the pasted transactions —
 * loading restores mapping and options through `onApply` while the input
 * stays exactly as the reader left it, and every storage failure surfaces as
 * a translated notice with the current input intact.
 *
 * A load first checks the profile against the current file
 * (`checkProfileCompatibility`): reordered columns apply cleanly by header
 * name, but missing or duplicated headers and a profile saved under another
 * preset apply only their resolvable parts behind a review notice — never a
 * silent mis-map. Each successful action also emits one bounded
 * `csv_profile` count event (save, clean load, delete, mismatch, or a
 * portable-file export/import outcome); the
 * payload carries only the action enum and the preset slug, never profile
 * names, headers, accounts or CSV text.
 *
 * All controls are native selects, inputs and buttons, so selection,
 * overwrite (Update) and deletion work by keyboard with no extra wiring.
 */
export function CsvProfileControls({
  headers,
  presetSlug,
  settings,
  onApply,
  storage,
}: {
  /** The parsed file's headers, or `null` before any headered input. */
  headers: readonly string[] | null;
  /** The current route's preset slug, or `null` on the generic converter. */
  presetSlug: string | null;
  /** The live widget settings a save snapshots. */
  settings: CsvProfileSettings;
  /** Apply a saved profile to the widget. Never receives statement data. */
  onApply: (profile: CsvMappingProfile) => void;
  /** Injected store for tests; resolves to `localStorage` when omitted. */
  storage?: ProfileStorage | null;
}) {
  const { t } = useTranslation();
  const store = storage === undefined ? resolveProfileStorage() : storage;

  const [profiles, setProfiles] = useState<CsvMappingProfile[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [notice, setNotice] = useState<
    { tone: "ok" | "error"; text: string } | null
  >(null);

  // Same post-hydration store sync as the theme provider: the first render
  // is empty on both server and client (no hydration mismatch), then the
  // browser store fills in once effects run.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setProfiles(loadMappingProfiles(store));
  }, [store]);

  const selected = profiles.find((profile) => profile.id === selectedId) ?? null;
  const displayName = (profile: CsvMappingProfile): string =>
    profile.label === "" ? t("tools.csvWidget.profiles.untitled") : profile.label;
  // Names need a header vocabulary to snapshot against; without pasted
  // headers there is nothing a reload could resolve.
  const canSave = headers !== null && headers.length > 0;

  /**
   * Write `next` to the store, reporting success so callers only move their
   * selection and name state when the profiles actually persisted — a failed
   * save leaves everything the reader typed exactly where it was.
   */
  function persist(
    next: CsvMappingProfile[],
    okKey: string,
    profileName: string,
  ): boolean {
    const result: SaveProfilesResult = saveMappingProfiles(next, store);
    if (!result.ok) {
      setNotice({ tone: "error", text: t(SAVE_PROFILES_ERROR_KEYS[result.reason]) });
      return false;
    }
    setProfiles(next);
    setNotice({ tone: "ok", text: t(okKey, { name: profileName }) });
    return true;
  }

  function snapshot(selectedProfile: CsvMappingProfile | null): CsvMappingProfile | null {
    const vocabulary = headers === null ? [] : snapshotHeaders(headers);
    if (headers === null || vocabulary.length === 0) {
      setNotice({
        tone: "error",
        text: t("tools.csvWidget.profiles.errors.noHeaders"),
      });
      return null;
    }
    if (!settingsValid(settings)) {
      setNotice({
        tone: "error",
        text: t("tools.csvWidget.profiles.errors.invalid"),
      });
      return null;
    }
    // The category column snapshots by header name with every other field
    // (see `indexMappingToHeaderNames`); the assignments persist here, in
    // the config, on explicit Save only — never rows, output or history.
    const categoryAccounts = snapshotCategoryAccounts(
      settings.categoryAccounts,
    );
    // Description rules persist the same way: order, identity and enabled
    // state as-is, on explicit Save/Update only — never analytics, URLs,
    // rows or output text. No rules reads as the pre-rules default, not an
    // empty array worth serializing back.
    const descriptionRules = snapshotDescriptionRules(
      settings.descriptionRules,
    );
    return {
      version: CSV_PROFILE_VERSION,
      id: selectedProfile?.id ?? createProfileId(),
      label: selectedProfile?.label ?? "",
      presetSlug,
      headers: vocabulary,
      mapping: indexMappingToHeaderNames(settings.mapping, headers),
      config: {
        sourceAccount: settings.sourceAccount.trim(),
        counterAccount: settings.counterAccount.trim(),
        // No assignments reads as the version-1 default — not an empty
        // object worth serializing back.
        ...(Object.keys(categoryAccounts).length > 0
          ? { categoryAccounts }
          : {}),
        ...(descriptionRules.length > 0 ? { descriptionRules } : {}),
        currency: settings.currency.trim() || DEFAULT_CURRENCY,
        invertAmounts: settings.invertAmounts,
        ...(settings.dateOrder === undefined
          ? {}
          : { dateOrder: settings.dateOrder }),
        outputMode: settings.outputMode,
      },
    };
  }

  function handleSave(): void {
    const label = name.trim();
    if (label === "") {
      setNotice({
        tone: "error",
        text: t("tools.csvWidget.profiles.errors.noName"),
      });
      return;
    }
    const profile = snapshot(null);
    if (profile === null) return;
    profile.label = label.slice(0, MAX_CSV_PROFILE_LABEL_LENGTH);
    if (
      !persist(
        [...profiles, profile],
        "tools.csvWidget.profiles.saved",
        profile.label,
      )
    ) {
      return;
    }
    trackCsvProfileAction("save", presetSlug);
    setSelectedId(profile.id);
    setName("");
  }

  function handleUpdate(): void {
    if (selected === null) return;
    const profile = snapshot(selected);
    if (profile === null) return;
    // A typed name renames; an empty field keeps the existing label.
    const label = name.trim();
    profile.label =
      label === ""
        ? selected.label
        : label.slice(0, MAX_CSV_PROFILE_LABEL_LENGTH);
    if (
      !persist(
        profiles.map((entry) => (entry.id === selected.id ? profile : entry)),
        "tools.csvWidget.profiles.updated",
        displayName(profile),
      )
    ) {
      return;
    }
    // An overwrite is a save the reader kept, not a new profile.
    trackCsvProfileAction("save", presetSlug);
    if (label !== "") setName("");
  }

  function handleLoad(): void {
    if (selected === null) return;
    applyWithReview(selected, "saved");
  }

  /**
   * Apply one profile through the compatibility review. Shared by Load (a
   * saved profile) and the file import's explicit Apply (a validated staged
   * profile that may never have been saved — storage blocked or full, or the
   * reader simply chose not to keep it). Only saved loads count toward the
   * existing `load`/`mismatch` events, whose meaning stays "reused a saved
   * profile"; file outcomes get their own `import_*` actions.
   */
  function applyWithReview(
    profile: CsvMappingProfile,
    origin: "saved" | "file",
  ): void {
    const compat: ProfileCompatibility = checkProfileCompatibility(
      profile,
      headers,
      presetSlug,
    );
    onApply(profile);
    trackCsvProfileAction(
      origin === "file" ? "import_apply" : compat.compatible ? "load" : "mismatch",
      presetSlug,
    );
    if (compat.compatible) {
      setNotice({
        tone: "ok",
        text: t("tools.csvWidget.profiles.loaded", {
          name: displayName(profile),
        }),
      });
      return;
    }
    // Incompatible, but never silently mis-mapped: the widget applied only
    // the resolvable columns (missing/duplicate names landed unmapped) and
    // kept this export's sign setting on a preset mismatch. Say exactly what
    // needs review — the column case first, since nothing converts until the
    // mapping is fixed; the preset case only matters once it does.
    const columns = [...compat.missingHeaders, ...compat.duplicateHeaders];
    setNotice({
      tone: "error",
      text:
        columns.length > 0
          ? t("tools.csvWidget.profiles.reviewColumns", {
              name: displayName(profile),
              columns: columns.join(", "),
            })
          : t("tools.csvWidget.profiles.reviewPreset", {
              name: displayName(profile),
            }),
    });
  }

  /**
   * The file import's one storage write: the whole chosen collection or
   * nothing. The in-memory list moves only once the store accepted it, so a
   * blocked or full store leaves the saved profiles exactly as they were.
   */
  function commitImport(next: CsvMappingProfile[]): SaveProfilesResult {
    const result = saveMappingProfiles(next, store);
    if (result.ok) {
      setProfiles(next);
      setNotice(null);
    }
    return result;
  }

  /**
   * Download profiles as a portable file through the strict
   * `@/lib/csv-profile-file` envelope.
   * Needs no storage: `current` snapshots the live settings (the same
   * validity gate as Save), so a blocked store still leaves a backup path.
   * Only the allowlisted profile shape is serialized — never rows, split
   * allocations, statement fields or output. Counted only once the download
   * actually started; a refused snapshot or serialization emits nothing.
   */
  function handleExport(scope: CsvProfileExportScope): void {
    let chosen: CsvMappingProfile[];
    if (scope === "current") {
      const profile = snapshot(null);
      if (profile === null) return;
      profile.label = name.trim().slice(0, MAX_CSV_PROFILE_LABEL_LENGTH);
      chosen = [profile];
    } else if (scope === "selected") {
      if (selected === null) return;
      chosen = [selected];
    } else {
      chosen = profiles;
    }
    const file = serializeCsvProfileFile(chosen);
    if (!file.ok) {
      setNotice({
        tone: "error",
        text: t("toolsCsvProfileFile.issue.invalidProfile"),
      });
      return;
    }
    downloadTextFile(file.text, file.fileName, file.mediaType);
    trackCsvProfileAction("export", presetSlug, scope);
    setNotice({
      tone: "ok",
      text: t("toolsCsvProfileFile.exported", { file: file.fileName }),
    });
  }

  function handleDelete(): void {
    if (selected === null) return;
    if (
      !persist(
        profiles.filter((entry) => entry.id !== selected.id),
        "tools.csvWidget.profiles.deleted",
        displayName(selected),
      )
    ) {
      return;
    }
    trackCsvProfileAction("delete", presetSlug);
    setSelectedId("");
  }

  return (
    <section>
      <h2 className="tool-settings-heading">
        {t("tools.csvWidget.profiles.title")}
      </h2>
      <p className="tool-local-note">
        {t("tools.csvWidget.profiles.localOnly")}
      </p>
      {profiles.length === 0 ? (
        <p className="tool-local-note">
          {t("tools.csvWidget.profiles.empty")}
        </p>
      ) : (
        <>
          <div className="tool-settings-grid">
            <label className="tool-control-label">
              <span className="tool-label-text">
                {t("tools.csvWidget.profiles.selectLabel")}
              </span>
              <select
                value={selectedId}
                onChange={(event) => {
                  setSelectedId(event.target.value);
                  setNotice(null);
                }}
                className="tool-control"
              >
                <option value="">
                  {t("tools.csvWidget.profiles.choose")}
                </option>
                {profiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {displayName(profile)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="tool-file-actions tool-settings-start">
            <button type="button" onClick={handleLoad} disabled={!selected}>
              {t("tools.csvWidget.profiles.load")}
            </button>
            <button
              type="button"
              onClick={handleUpdate}
              disabled={!selected || !canSave}
            >
              {t("tools.csvWidget.profiles.update")}
            </button>
            <button type="button" onClick={handleDelete} disabled={!selected}>
              {t("tools.csvWidget.profiles.delete")}
            </button>
            <button
              type="button"
              onClick={() => handleExport("selected")}
              disabled={!selected}
            >
              {t("toolsCsvProfileFile.exportSelected")}
            </button>
            {profiles.length > 1 && (
              <button type="button" onClick={() => handleExport("all")}>
                {t("toolsCsvProfileFile.exportAll")}
              </button>
            )}
          </div>
        </>
      )}
      <div className="tool-settings-grid tool-settings-start">
        <label className="tool-control-label">
          <span className="tool-label-text">
            {t("tools.csvWidget.profiles.nameLabel")}
          </span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={MAX_CSV_PROFILE_LABEL_LENGTH}
            placeholder={t("tools.csvWidget.profiles.namePlaceholder")}
            autoComplete="off"
            className="tool-control"
          />
        </label>
      </div>
      <div className="tool-file-actions tool-settings-start">
        <button type="button" onClick={handleSave} disabled={!canSave}>
          {t("tools.csvWidget.profiles.save")}
        </button>
        <button
          type="button"
          onClick={() => handleExport("current")}
          disabled={!canSave}
        >
          {t("toolsCsvProfileFile.exportCurrent")}
        </button>
      </div>
      <CsvProfileImportControls
        saved={profiles}
        headers={headers}
        presetSlug={presetSlug}
        displayName={displayName}
        onCommit={commitImport}
        onApply={(profile) => applyWithReview(profile, "file")}
      />
      {profiles.length >= MAX_CSV_PROFILES && (
        <p className="tool-local-note">
          {t("tools.csvWidget.profiles.errors.tooMany")}
        </p>
      )}
      {notice !== null && (
        <p
          role={notice.tone === "error" ? "alert" : "status"}
          className={notice.tone === "error" ? "tool-notice" : "tool-status"}
        >
          {notice.text}
        </p>
      )}
    </section>
  );
}
